import React from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { TFn } from './rccTypes';

function bulletsFromText(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function AiBulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5">
      {items.map((b, i) => (
        <li key={i} className="flex gap-2 text-xs leading-relaxed text-slate-600">
          <span className="shrink-0 font-medium text-pink-400" aria-hidden>
            →
          </span>
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

/** Pink-tinted container that brands the two AI panels as one "AI Insights" block. */
function AiInsightsShell({ t, children }: { t: TFn; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-pink-100 bg-gradient-to-br from-pink-50/70 via-white to-white p-2.5">
      <div className="mb-2 flex items-center gap-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-pink-600">
        <Sparkles className="size-3" aria-hidden />
        {t('card.ai_insights')}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">{children}</div>
    </div>
  );
}

/** Loading placeholder mirroring the Summary | Fit panels while AI analyzes. */
export function AiResultSkeleton({ t, className }: { t: TFn; className?: string }) {
  return (
    <div className={cn(className)} aria-busy="true" aria-live="polite">
      <AiInsightsShell t={t}>
        {[t('card.summary'), t('card.fit')].map((label) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {label}
            </div>
            <div className="space-y-2">
              <div className="h-2.5 w-full animate-pulse rounded bg-slate-100" />
              <div className="h-2.5 w-5/6 animate-pulse rounded bg-slate-100" />
              <div className="h-2.5 w-3/4 animate-pulse rounded bg-slate-100" />
            </div>
          </div>
        ))}
      </AiInsightsShell>
    </div>
  );
}

/** AI panels: side-by-side Summary | Fit inside a soft pink-branded shell. */
export function AiResultPanels({
  summary,
  fit,
  t,
  className,
}: {
  summary: string;
  fit: string;
  t: TFn;
  className?: string;
}) {
  return (
    <div className={cn(className)}>
      <AiInsightsShell t={t}>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            {t('card.summary')}
          </div>
          <AiBulletList items={bulletsFromText(summary)} />
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
            {t('card.fit')}
          </div>
          <AiBulletList items={bulletsFromText(fit)} />
        </div>
      </AiInsightsShell>
    </div>
  );
}
